import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProfilePicture from './ProfilePicture';

const TICKER_ITEMS = [
  "🕉️ सीता राम प्रथम मिलन — पुष्प वाटिका, फुलहर धाम",
  "🏗️ ₹31.13 करोड़ की विकास परियोजना प्रगति पर",
  "🗺️ रामायण सर्किट — बिहार सरकार मान्यता प्राप्त",
  "🪷 मिथिला मध्य परिक्रमा का प्रमुख पड़ाव — गिरिजा स्थान",
  "📞 संपर्क: +91 9470820521  |  shreesitaramjimandir@gmail.com",
  "🏛️ NGO Darpan: BR/2025/0853517  |  Trust No: 746",
];

const NAV_LINKS = [
  { to: "/",        label: "होम" },
  { to: "/about",   label: "हमारे बारे में" },
  { to: "/blog",    label: "ब्लॉग" },
  { to: "/news",    label: "समाचार" },
  { to: "/gallery", label: "गैलरी" },
  { to: "/donate",  label: "दान करें" },
  { to: "/contact", label: "संपर्क" },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Scroll detection
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    setShowProfileDropdown(false);
    setMenuOpen(false);
    navigate('/');
  };

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      {/* ── Announcement Ticker ── */}
      <div className="ticker-wrap" style={{ minHeight: '28px' }}>
        <div className="ticker-content">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i}>{item}</span>
          ))}
        </div>
      </div>

      {/* ── Main Navbar ── */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-400 ${
          scrolled ? 'navbar-solid' : 'bg-white shadow-sm border-b border-temple-secondary/10'
        }`}
        style={{ transition: 'all 0.4s ease' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          <div className="flex items-center justify-between h-16">

            {/* ── Logo ── */}
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
                style={{ background: 'linear-gradient(135deg, #8B1A1A, #E8621A, #F4A500)' }}
              >
                🕉️
              </div>
              <div className="flex flex-col leading-none">
                <span
                  className="font-serif text-xl md:text-2xl font-bold leading-none"
                  style={{ color: '#C44E0E' }}
                >
                  फुलहर धाम
                </span>
                <span className="text-[10px] font-semibold tracking-widest uppercase mt-0.5"
                  style={{ color: '#7A5540' }}>
                  तीर्थ क्षेत्र ट्रस्ट
                </span>
              </div>
            </Link>

            {/* ── Desktop Menu ── */}
            <div className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`nav-link px-3 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${
                    isActive(to)
                      ? 'active bg-temple-primary/8 text-temple-primary'
                      : 'text-temple-text hover:text-temple-primary hover:bg-temple-primary/5'
                  }`}
                >
                  {label}
                </Link>
              ))}

              {/* Donate CTA */}
              <Link
                to="/donate"
                className="ml-2 px-4 py-2 rounded-xl text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #8B1A1A, #E8621A)', boxShadow: '0 3px 12px rgba(139,26,26,0.3)' }}
              >
                💰 दान करें
              </Link>

              {/* Auth area */}
              {user ? (
                <div className="relative ml-2" ref={dropdownRef}>
                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-temple-secondary/20 hover:border-temple-secondary/50 transition-all bg-temple-bg"
                  >
                    <ProfilePicture profilePicture={user.profilePicture} name={user.name} size="small" editable={false} />
                    <span className="text-sm font-semibold text-temple-text max-w-[100px] truncate">{user.name}</span>
                    <i className={`fas fa-chevron-down text-xs text-temple-text-muted transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showProfileDropdown && (
                    <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-temple-lg border border-temple-secondary/15 py-2 z-50 animate-fade-in">
                      <div className="px-4 py-3 border-b border-temple-secondary/10">
                        <p className="text-sm font-bold text-temple-text">{user.name}</p>
                        <p className="text-xs text-temple-text-muted">{user.email}</p>
                        {user.role && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-bold rounded-full"
                            style={{ background: 'rgba(232,98,26,0.1)', color: '#E8621A' }}>
                            {user.role === 'admin' ? '🛡️ व्यवस्थापक' : '🙏 भक्त'}
                          </span>
                        )}
                      </div>
                      <div className="py-1">
                        {[
                          { to: '/dashboard', icon: 'fa-tachometer-alt', label: 'डैशबोर्ड' },
                          { to: '/profile',   icon: 'fa-user-edit',      label: 'प्रोफ़ाइल' },
                        ].map(({ to, icon, label }) => (
                          <Link key={to} to={to} onClick={() => setShowProfileDropdown(false)}
                            className="flex items-center px-4 py-2.5 text-sm text-temple-text hover:bg-temple-bg transition-colors">
                            <i className={`fas ${icon} mr-3 text-temple-primary text-sm`} />
                            {label}
                          </Link>
                        ))}
                        <div className="border-t border-temple-secondary/10 my-1" />
                        <button onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors">
                          <i className="fas fa-sign-out-alt mr-3" /> लॉगआउट
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 ml-2">
                  <Link to="/login"
                    className="px-4 py-2 rounded-xl text-sm font-bold border-2 border-temple-primary text-temple-primary hover:bg-temple-primary hover:text-white transition-all duration-200">
                    लॉगिन
                  </Link>
                  <Link to="/signup"
                    className="px-4 py-2 rounded-xl text-sm font-bold text-white transition-all duration-200"
                    style={{ background: 'linear-gradient(135deg, #E8621A, #C44E0E)' }}>
                    साइनअप
                  </Link>
                </div>
              )}
            </div>

            {/* ── Mobile: Donate + Hamburger ── */}
            <div className="lg:hidden flex items-center gap-2">
              <Link to="/donate"
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #8B1A1A, #E8621A)' }}>
                💰 दान
              </Link>
              <button
                onClick={() => setMenuOpen(true)}
                className="p-2 rounded-lg text-temple-text hover:text-temple-primary hover:bg-temple-primary/8 transition-colors"
                aria-label="Menu खोलें"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Mobile Overlay ── */}
      <div
        className={`mobile-overlay ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* ── Mobile Slide-In Menu ── */}
      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-temple-secondary/15"
          style={{ background: 'linear-gradient(135deg, #8B1A1A, #C44E0E)' }}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🕉️</span>
            <div>
              <p className="font-serif font-bold text-white text-lg leading-none">फुलहर धाम</p>
              <p className="text-white/70 text-[10px] tracking-widest uppercase">तीर्थ क्षेत्र ट्रस्ट</p>
            </div>
          </div>
          <button onClick={() => setMenuOpen(false)}
            className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Nav Links */}
        <div className="p-4 space-y-1">
          {NAV_LINKS.map(({ to, label }) => (
            <Link key={to} to={to}
              className={`flex items-center px-4 py-3 rounded-xl text-base font-semibold transition-all ${
                isActive(to)
                  ? 'text-temple-primary bg-temple-primary/10 border border-temple-primary/20'
                  : 'text-temple-text hover:text-temple-primary hover:bg-temple-primary/5'
              }`}>
              {isActive(to) && <span className="w-2 h-2 rounded-full bg-temple-primary mr-3 flex-shrink-0" />}
              {label}
            </Link>
          ))}
        </div>

        {/* Divider */}
        <div className="mx-4 border-t border-temple-secondary/15" />

        {/* Auth */}
        <div className="p-4">
          {user ? (
            <>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-temple-bg-soft mb-3">
                <ProfilePicture profilePicture={user.profilePicture} name={user.name} size="small" editable={false} />
                <div>
                  <p className="text-sm font-bold text-temple-text">{user.name}</p>
                  <p className="text-xs text-temple-text-muted">{user.email}</p>
                </div>
              </div>
              {[
                { to: '/dashboard', icon: 'fa-tachometer-alt', label: 'डैशबोर्ड' },
                { to: '/profile',   icon: 'fa-user-edit',      label: 'प्रोफ़ाइल' },
              ].map(({ to, icon, label }) => (
                <Link key={to} to={to}
                  className="flex items-center px-4 py-3 rounded-xl text-sm font-semibold text-temple-text hover:bg-temple-bg transition-colors mb-1">
                  <i className={`fas ${icon} mr-3 text-temple-primary`} /> {label}
                </Link>
              ))}
              <button onClick={handleLogout}
                className="flex items-center w-full px-4 py-3 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors mt-1">
                <i className="fas fa-sign-out-alt mr-3" /> लॉगआउट
              </button>
            </>
          ) : (
            <div className="space-y-3">
              <Link to="/login"
                className="block text-center py-3 rounded-xl font-bold text-temple-primary border-2 border-temple-primary hover:bg-temple-primary hover:text-white transition-all">
                लॉगिन
              </Link>
              <Link to="/signup"
                className="block text-center py-3 rounded-xl font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #E8621A, #C44E0E)' }}>
                साइनअप — भक्त बनें
              </Link>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="mx-4 mt-2 p-3 rounded-xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(232,98,26,0.06), rgba(244,165,0,0.06))', border: '1px solid rgba(244,165,0,0.15)' }}>
          <p className="text-xs text-temple-text-muted font-medium">📞 +91 9470820521</p>
          <p className="text-[10px] text-temple-text-faint mt-1">NGO Darpan: BR/2025/0853517</p>
        </div>
      </div>
    </>
  );
};

export default Navbar;